import { ApiInstance, scopedTo, unscoped } from '@/lib/api'
import { cursorParams, type Paginated } from '@/lib/pagination'
import { getStatusConfig } from '@/lib/subscription'
import type { Labeled } from '@/lib/tone'
import type { SubscriptionStatus } from '@/pages/Companies/GetCompany/SubscriptionSettings/types'
import type { CompanyData } from '@/pages/Companies/GetCompany/GeneralSettings/types'
import type { CompanyConfig } from '@/pages/Companies/GetCompany/types/config'
import type { CompanyWithConfigResponse } from '@/pages/Companies/GetCompany/types/api'

export interface CompanyDomain {
  id: string
  companyId: string
  url: string
  isVerified: boolean
  cloudflareHostnameId?: string | null
}

export interface CompanyConfigResponse<Body = Record<string, unknown>> {
  id: string
  companyId: string
  title: string
  description: string
  body: Body
  domains: CompanyDomain[]
}

export const companyConfigQueryKey = (companyId?: string | null) => ['company-config', companyId ?? '']

export const companyDataQueryKey = (companyId?: string | null) => ['company', companyId ?? '']

export function flattenConfig<Config = CompanyConfig>(raw: CompanyConfigResponse | null | undefined): Config {
  return {
    ...(raw?.body ?? {}),
    title: raw?.title,
    description: raw?.description,
    urls: (raw?.domains ?? []).map(({ url, isVerified }) => ({ url, isVerified }))
  } as Config
}

export async function fetchCompanyConfig<Config = CompanyConfig>(companyId?: string): Promise<Config> {
  const { data } = await ApiInstance.get<CompanyConfigResponse>('/config/companies', scopedTo(companyId))
  return flattenConfig<Config>(data)
}

export async function fetchCompanyData(companyId?: string): Promise<CompanyData> {
  const { data } = await ApiInstance.get<CompanyData>('/companies', scopedTo(companyId))
  return data
}

export async function fetchCompanyWithConfig<Config = CompanyConfig>(companyId?: string): Promise<CompanyWithConfigResponse<Config>> {
  const [company, config] = await Promise.all([fetchCompanyData(companyId), fetchCompanyConfig<Config>(companyId)])
  return { company, config }
}

export async function updateCompanyConfig(payload: Record<string, unknown>, companyId?: string) {
  const { data } = await ApiInstance.put<CompanyConfigResponse>('/config/companies', payload, scopedTo(companyId))
  return data
}

export interface AdminCompany {
  id: string
  name: string
  cnpj: string | null
  isActive: boolean
  subscriptionPlan: string
  subscriptionStatus: string
  subscriptionEndDate: string | null
  membersCount: number
  createdAt: string
  updatedAt: string
}

export interface AdminCompaniesParams {
  cursor?: string
  perPage?: number
  search?: string
  sort?: string
}

export function companySearchTerm(value?: string | null): string | undefined {
  const term = (value ?? '').trim()
  if (!term) return undefined
  const looksLikeDocument = /^[\d./\s-]+$/.test(term) && /\d/.test(term)
  return looksLikeDocument ? term.replace(/\D/g, '') : term
}

export async function listAdminCompanies(params: AdminCompaniesParams): Promise<Paginated<AdminCompany>> {
  const { data } = await ApiInstance.get<Paginated<AdminCompany>>('/admin/companies', {
    ...unscoped(),
    params: { ...cursorParams(params.cursor, params.perPage), search: companySearchTerm(params.search), sort: params.sort }
  })
  return data
}

export const COMPANY_SITUATION: Record<'active' | 'inactive', Labeled> = {
  active: { label: 'Ativa', tone: 'success' },
  inactive: { label: 'Desativada', tone: 'neutral' }
}

export const companySituation = (isActive?: boolean | null): Labeled => (isActive === false ? COMPANY_SITUATION.inactive : COMPANY_SITUATION.active)

const EXPIRED_SUBSCRIPTION: Labeled = { label: 'Expirada', tone: 'danger' }

export function companySubscription(status?: string | null, endDate?: string | null, now: number = Date.now()): Labeled {
  const ended = !!endDate && new Date(endDate).getTime() < now
  if (ended && (status === 'active' || status === 'trialing')) return EXPIRED_SUBSCRIPTION
  const { label, tone } = getStatusConfig((status ?? undefined) as SubscriptionStatus | undefined)
  return { label, tone }
}

export function mergeCompanies<T extends { id: string }>(first: T[], second: T[]): T[] {
  const seen = new Set(first.map(company => company.id))
  return [...first, ...second.filter(company => !seen.has(company.id))]
}

export interface PendingFlags {
  isNew?: boolean
  isUpdated?: boolean
  isDeleted?: boolean
}

export interface PendingHandlers<T> {
  create: (item: T) => Promise<Partial<T>>
  update?: (item: T) => Promise<Partial<T>>
  remove: (item: T) => Promise<unknown>
}

export interface PendingCommit<T> {
  items: T[]
  error?: unknown
}

const settled = <T extends PendingFlags>(item: T): T => ({ ...item, isNew: false, isUpdated: false, isDeleted: false })

export const hasPendingItems = (items: PendingFlags[]): boolean => items.some(item => item.isNew || item.isUpdated || item.isDeleted)

export async function commitPendingItems<T extends PendingFlags>(items: T[], handlers: PendingHandlers<T>): Promise<PendingCommit<T>> {
  const done: T[] = []
  for (let index = 0; index < items.length; index++) {
    const item = items[index]
    try {
      if (item.isDeleted) {
        if (!item.isNew) await handlers.remove(item)
        continue
      }
      if (item.isNew) done.push(settled({ ...item, ...(await handlers.create(item)) }))
      else if (item.isUpdated && handlers.update) done.push(settled({ ...item, ...(await handlers.update(item)) }))
      else done.push(settled(item))
    } catch (error) {
      return { items: [...done, ...items.slice(index)], error }
    }
  }
  return { items: done }
}

